// Recovery handler khi verify remote fail — thay vì throw văng init, prompt user
// xử lý phù hợp với reason code:
//   - no-access: có thể wrong gh account → offer switch (gh auth login)
//   - not-found: URL sai → user re-nhập URL đúng (v1.2.7)
//   - network:   user check mạng/VPN, retry
//   - timeout:   tương tự network
//
// Loop retry cho phép user thử nhiều lần (switch account, re-nhập URL, accept invite, ...).
// Return { resolvedUrl } khi access OK (URL có thể khác input nếu user re-nhập).
// Throw RemoteAccessAbortedError khi user abort.
//
// v1.2.7: Thêm option "Nhập lại URL" để user fix URL sai mà không phải abort init.
import { spawnSync } from "node:child_process";
import { input, select } from "@inquirer/prompts";
import { isGithubHost, parseGitRemoteHost } from "./git-remote-host.js";
import { promptAndStorePatForHost } from "./prompt-and-store-pat-for-non-github-host.js";
import { resetFolderGitAndCreateNewRemoteUnderCurrentUser } from "./reset-folder-git-and-create-new-remote-under-current-user.js";
import { log } from "./terminal-logger.js";
import {
  type RemoteFailReason,
  tryVerifyGitRemoteAccessible,
} from "./verify-git-remote-accessible.js";

// Custom error báo user abort recovery — caller catch để exit 0 graceful
// (giống pattern TeamPackAccessAbortedError v1.2.3).
export class RemoteAccessAbortedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RemoteAccessAbortedError";
  }
}

// Resolve gh user hiện tại (nếu có) để show context cho user khi prompt.
function getCurrentGhUser(): string | null {
  const r = spawnSync("gh", ["api", "user", "--jq", ".login"], {
    encoding: "utf8",
    stdio: ["ignore", "pipe", "pipe"],
  });
  if (r.status !== 0) return null;
  return r.stdout.trim() || null;
}

// Trigger `gh auth login` interactive — block đến khi user complete browser.
// Sau khi switch, gh CLI dùng account mới cho mọi command sau.
function triggerGhAuthLoginInteractive(): void {
  log.info("Mở browser để switch GitHub account...");
  const r = spawnSync("gh", ["auth", "login", "--web"], { stdio: "inherit" });
  if (r.status !== 0) {
    log.warn(`gh auth login exit ${r.status}. Bạn có thể chạy tay rồi quay lại retry.`);
  }
}

// Hint theo reason code — giúp user biết phải làm gì cụ thể.
function getReasonHint(reason: RemoteFailReason, url: string, ghUser: string | null): string {
  switch (reason) {
    case "no-access":
      return ghUser
        ? `Repo có thể private và account '${ghUser}' không có quyền access. Switch sang account có quyền, hoặc xin invite từ owner repo.`
        : "Repo có thể private và gh CLI chưa login. Login trước rồi retry.";
    case "not-found":
      return `URL có thể sai chính tả, hoặc repo đã bị xóa/rename. Nhập lại URL đúng. Check: ${url}`;
    case "network":
      return "Không kết nối được GitHub. Check mạng / VPN / firewall.";
    case "timeout":
      return "Network chậm > 5s. Check mạng rồi retry.";
    default:
      return "Lỗi không xác định. URL có thể sai — nhập lại, hoặc check gh auth status.";
  }
}

// Validate URL format git remote (http/https/ssh github).
function isValidGitUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  return (
    /^https?:\/\/[\w.@/-]+$/.test(trimmed) ||
    /^git@[\w.-]+:[\w./-]+\.git$/.test(trimmed) ||
    /^[\w.-]+\/[\w.-]+$/.test(trimmed) // owner/repo shorthand
  );
}

// Show menu recovery options theo reason. Loop cho đến khi user pick abort
// hoặc remote accessible. Return resolved URL (có thể khác input nếu user re-nhập).
// Throw RemoteAccessAbortedError nếu user abort.
//
// v1.2.7: Thêm option "Nhập lại URL" để user fix URL sai mà không phải abort init.
// v1.12.0: Thêm option "Reset & make folder mine" — chỉ enable khi caller pass
//   folderPath (tức flow existing-folder). Cho phép user clone folder team khác
//   xuống làm khởi đầu project mới của mình mà không bị block bởi remote không access.
export async function handleRemoteAccessFailureWithAccountSwitch(args: {
  url: string;
  initialReason: RemoteFailReason;
  initialDetail?: string;
  // v1.12.0: Khi caller pass folderPath (existing-folder flow), enable option
  // "Reset folder & tạo repo mới". Khi không có (existing-remote flow), folder
  // chưa tồn tại nên option này không áp dụng.
  folderPath?: string;
  // v1.12.0: Skip prompt khi caller đã có visibility từ --repo-visibility.
  defaultVisibility?: "private" | "public";
}): Promise<{ resolvedUrl: string }> {
  let currentUrl = args.url;
  let reason = args.initialReason;
  let detail = args.initialDetail;

  while (true) {
    const ghUser = getCurrentGhUser();
    log.warn(`Không truy cập được ${currentUrl}`);
    log.dim(`  Lý do: ${reason}${detail ? ` — ${detail.slice(0, 150)}` : ""}`);
    log.info(getReasonHint(reason, currentUrl, ghUser));
    if (ghUser) log.dim(`  gh CLI hiện đang login: ${ghUser}`);

    // Detect host để quyết định option credential phù hợp
    const host = parseGitRemoteHost(currentUrl);
    const isGh = isGithubHost(host);
    const authReasons = new Set<RemoteFailReason>(["no-access", "unknown", "timeout"]);

    // Choices base — 4 option có sẵn từ v1.2.7.
    const choices: Array<{
      name: string;
      value: "re-input-url" | "switch" | "enter-pat" | "retry" | "reset-folder" | "abort";
    }> = [
      {
        name: "Nhập lại URL đúng (recommended khi URL sai chính tả)",
        value: "re-input-url",
      },
    ];

    if (authReasons.has(reason)) {
      if (isGh) {
        choices.push({
          name: "Switch GitHub account (gh auth login — mở browser)",
          value: "switch",
        });
      } else if (host) {
        choices.push({
          name: `Nhập Personal Access Token cho ${host} (lưu vào keychain)`,
          value: "enter-pat",
        });
      }
    }

    choices.push({
      name: "Tôi vừa fix (accept invite / sửa permission) — retry verify",
      value: "retry",
    });

    // v1.12.0: Conditional option — chỉ show khi caller pass folderPath.
    // Common case: clone folder team khác xuống, không có quyền push lên repo gốc,
    // muốn dùng làm khởi đầu project riêng.
    if (args.folderPath) {
      choices.push({
        name: "Reset folder & tạo repo MỚI dưới account của tôi (backup .git cũ)",
        value: "reset-folder",
      });
    }

    choices.push({
      name: "Tạm ngưng init — chạy lại 'avatar init' sau",
      value: "abort",
    });

    const action = await select({ message: "Cách xử lý?", choices });

    if (action === "abort") {
      throw new RemoteAccessAbortedError(
        `User chọn tạm ngưng. Fix access ${currentUrl} rồi chạy lại 'avatar init'.`,
      );
    }

    if (action === "reset-folder") {
      // Defensive: folderPath required cho branch này — đã guard ở choices push,
      // nhưng TS không narrow qua select() callback.
      if (!args.folderPath) {
        log.warn("Reset folder cần folderPath — internal error.");
        continue;
      }
      try {
        const reset = await resetFolderGitAndCreateNewRemoteUnderCurrentUser({
          folderPath: args.folderPath,
          visibility: args.defaultVisibility,
        });
        log.success(`Folder đã reset. Backup tại: ${reset.backupPath}`);
        log.success(`Remote mới: ${reset.newRemoteUrl}`);
        return { resolvedUrl: reset.newRemoteUrl };
      } catch (err) {
        log.warn(`Reset folder thất bại: ${(err as Error).message}`);
        // Loop tiếp — user có thể chọn option khác.
        continue;
      }
    }

    if (action === "re-input-url") {
      const newUrl = await input({
        message:
          "URL git remote (https://github.com/owner/repo.git hoặc git@github.com:owner/repo.git):",
        default: currentUrl,
        validate: (v) => isValidGitUrl(v) || "URL không đúng format git remote",
      });
      currentUrl = newUrl.trim();
    }

    if (action === "switch") {
      triggerGhAuthLoginInteractive();
    }

    if (action === "enter-pat") {
      // Non-GitHub host — prompt PAT + lưu vào keychain, rồi re-verify ngay.
      if (host) {
        await promptAndStorePatForHost(host);
      }
      // Fall through → re-verify bên dưới.
    }

    // Re-verify (re-input-url + switch + retry + enter-pat đều chạy qua đây).
    log.info(`Verify remote lại: ${currentUrl}...`);
    const result = tryVerifyGitRemoteAccessible(currentUrl);
    if (result.ok) {
      log.success(`Remote accessible: ${currentUrl}`);
      return { resolvedUrl: currentUrl };
    }
    // Fail tiếp — update reason + loop.
    reason = result.reason ?? "unknown";
    detail = result.detail;
  }
}
